import type { QuoteOutputProjection } from '../../../api/active/generated/quote';

type QuoteOutputRow = QuoteOutputProjection['rows'][number];

const GROUP_SUMMARY_COLUMN_KEYS = new Set([
  'group_name',
  'plan_package_price',
  'package_final_discounted',
  'package_original_price',
  'package_original_price_tax13',
  'package_discounted_quote',
  'package_discounted_quote_tax13',
  'package_discount_quote_tax13',
]);

/** 按实例首次出现位置稳定聚拢输出；独立设备和空方案各自保留，不改原数组。 */
export function stableQuoteOutputRows(
  rows: readonly QuoteOutputRow[],
): QuoteOutputRow[] {
  const ordered: QuoteOutputRow[][] = [];
  const groups = new Map<string, QuoteOutputRow[]>();
  for (const row of rows) {
    const id = row.rowKind === 'empty-plan' ? null : row.groupId;
    const existing = id ? groups.get(id) : undefined;
    if (existing) existing.push(row);
    else {
      const members = [row];
      ordered.push(members);
      if (id) groups.set(id, members);
    }
  }
  return ordered.flat();
}

export function isQuoteOutputGroupSummaryColumn(key: string): boolean {
  return GROUP_SUMMARY_COLUMN_KEYS.has(key);
}

export function quoteOutputRowKey(row: QuoteOutputRow): string {
  if (row.rowKind === 'empty-plan')
    return `empty-plan:${row.containerInstanceId}`;
  if (row.groupId !== null)
    return `quote-output:${JSON.stringify([row.itemId, row.groupId])}`;
  return String(row.itemId);
}

/** Resolve unique row keys while preserving the historical key for singleton rows. */
export function quoteOutputRowKeys(
  rows: readonly QuoteOutputRow[],
): (row: QuoteOutputRow) => string {
  const keysByRow = new Map<QuoteOutputRow, string>();
  const occurrences = new Map<string, number>();
  for (const row of rows) {
    const baseKey = quoteOutputRowKey(row);
    const occurrence = occurrences.get(baseKey) ?? 0;
    const key = occurrence ? `${baseKey}:duplicate:${occurrence}` : baseKey;
    occurrences.set(baseKey, occurrence + 1);
    keysByRow.set(row, key);
  }
  return (row) => keysByRow.get(row) ?? quoteOutputRowKey(row);
}

function groupName(row: QuoteOutputRow): string | null {
  const value = row.values.group_name;
  return typeof value === 'string' && value.trim() ? value : null;
}

function isSameContinuousGroup(
  left: QuoteOutputRow | undefined,
  right: QuoteOutputRow | undefined,
): boolean {
  if (left?.rowKind === 'empty-plan' || right?.rowKind === 'empty-plan')
    return false;
  if (!left?.groupId || !right?.groupId || left.groupId !== right.groupId)
    return false;
  const leftName = groupName(left);
  return leftName !== null && leftName === groupName(right);
}

/**
 * Presentation-only rowspan for a server-projected quotation table.
 *
 * A group exists only when adjacent rows carry the same non-empty groupId and
 * group_name. Ungrouped device rows therefore stay independent, and image or
 * per-device price columns are never merged.
 */
export function quoteOutputRowSpan(
  rows: readonly QuoteOutputRow[],
  rowIndex: number,
  columnKey: string,
): number {
  if (!GROUP_SUMMARY_COLUMN_KEYS.has(columnKey)) return 1;
  const row = rows[rowIndex];
  if (row?.rowKind === 'empty-plan' || !row?.groupId || groupName(row) === null)
    return 1;
  if (isSameContinuousGroup(rows[rowIndex - 1], row)) return 0;

  let span = 1;
  while (isSameContinuousGroup(row, rows[rowIndex + span])) span += 1;
  return span;
}

/** Mirror the frozen export sheet's displayed group cells for new public projections. */
export function quoteOutputPresentedGroupCells(
  projection: QuoteOutputProjection,
): Map<string, { rowSpan: number; value: string | number | null }> {
  const cells = new Map<
    string,
    { rowSpan: number; value: string | number | null }
  >();
  const existing = new Set(projection.columns.map((column) => column.key));
  const keys = ['group_name'];
  if (projection.fieldCombination.businessContent.startsWith('rental'))
    keys.push('package_original_price_tax13', 'package_discounted_quote_tax13');
  if (projection.fieldCombination.businessContent === 'default')
    keys.push(
      'plan_package_price',
      'package_final_discounted',
      'package_discounted_quote',
      'package_original_price',
    );
  const visibleKeys = keys.filter((key) => existing.has(key));
  for (const key of visibleKeys) {
    const identity = (index: number) =>
      quoteOutputSummaryIdentity(
        projection.rows[index],
        key,
        projection.fieldCombination.businessContent,
      );
    let start = 0;
    while (start < projection.rows.length) {
      const groupId = identity(start);
      let end = start;
      while (groupId !== null && identity(end + 1) === groupId) end += 1;
      if (groupId !== null && end > start) {
        let segmentStart = start;
        const applicable = (index: number) =>
          key === 'group_name' || projection.rows[index]?.values[key] !== '-';
        const present = projection.rows
          .slice(start, end + 1)
          .map((row) => row.values[key])
          .filter(
            (value) =>
              value !== null &&
              value !== undefined &&
              value !== '' &&
              (key === 'group_name' || value !== '-'),
          );
        const value = present[0] ?? null;
        // Preserve availability of malformed historical snapshots without hiding conflicting prices.
        if (present.every((entry) => entry === value)) {
          while (segmentStart <= end) {
            if (!applicable(segmentStart)) {
              segmentStart += 1;
              continue;
            }
            let segmentEnd = segmentStart;
            while (segmentEnd < end && applicable(segmentEnd + 1))
              segmentEnd += 1;
            if (segmentEnd > segmentStart)
              for (let index = segmentStart; index <= segmentEnd; index += 1)
                cells.set(`${index}:${key}`, {
                  rowSpan:
                    index === segmentStart ? segmentEnd - segmentStart + 1 : 0,
                  value,
                });
            segmentStart = segmentEnd + 1;
          }
        }
      }
      start = end + 1;
    }
  }
  return cells;
}

/** Names retain package identity; only frozen designated price summaries cross packages. */
export function quoteOutputSummaryIdentity(
  row: QuoteOutputRow | undefined,
  key: string,
  businessContent = 'default',
): string | null {
  if (!row || row.rowKind === 'empty-plan') return null;
  const priceColumns =
    businessContent === 'default'
      ? [
          'plan_package_price',
          'package_final_discounted',
          'package_discounted_quote',
          'package_original_price',
        ]
      : businessContent.startsWith('rental')
        ? ['package_original_price_tax13', 'package_discounted_quote_tax13']
        : [];
  return priceColumns.includes(key)
    ? (row.priceMergeGroupId ?? row.groupId)
    : row.groupId;
}
